import { ref } from "vue"

// Set by the global header search button to ask ExpensesView to open the
// advanced search panel as soon as it mounts (or immediately, if already
// mounted). ExpensesView resets this back to false once consumed.
export const pendingSearchOpen = ref(false)

export function requestSearchOpen() {
  pendingSearchOpen.value = true
}

// Set when an anonymous user taps a gated feature (AI assistant/insights) or
// "Upgrade now" in their profile. App.vue watches this and opens the
// AccountUpgrade *linking* modal — linking keeps the guest's data, unlike a
// plain register/login. App.vue resets it back to false once consumed.
export const pendingUpgradeOpen = ref(false)

export function requestAccountUpgrade() {
  pendingUpgradeOpen.value = true
}

// --- Global command palette state ---
export const globalSearchOpen = ref(false)

export function openGlobalSearch() {
  globalSearchOpen.value = true
}

export function closeGlobalSearch() {
  globalSearchOpen.value = false
}

// --- Pending action bus ---
// Views subscribe to this ref to react when the global search dispatches
// an action (e.g. "open the add-expense form"). The producer sets the
// payload; the consumer view clears it once handled.
export interface PendingAction {
  action: string
  focus?: { kind: string; id?: string } | null
  // monotonic counter to ensure repeated identical actions still trigger a watcher
  ts: number
}

export const pendingAction = ref<PendingAction | null>(null)

export function dispatchAction(action: string, focus?: PendingAction["focus"]) {
  pendingAction.value = { action, focus: focus ?? null, ts: Date.now() }
}

// --- Modal back-handler stack ---
// Overlay components (EditModal, BottomSheet, ConfirmDialog, ActionSheet, …)
// register their close function while open so the Android hardware back
// button can dismiss the topmost overlay instead of leaving the screen.
// Registration order is stacking order: the last overlay opened is the first
// one dismissed.
//
// A handler may return `false` to decline the press — used by multi-step
// overlays (onboarding) that stay on screen across several back presses and
// eventually run out of steps to go back through. Declining leaves the handler
// registered and lets the press fall through to the next one down, and finally
// to App.vue's tab/minimize behaviour. Returning anything else (including
// undefined) means "consumed".
type ModalBackHandler = () => boolean | void

const modalBackHandlers: ModalBackHandler[] = []

/**
 * Register `close` as the topmost back target. Returns an unregister function
 * the caller must invoke when the overlay closes by any other means (tap on
 * the backdrop, Escape, Save, unmount) so stale handlers don't accumulate.
 */
export function registerModalBackHandler(close: ModalBackHandler): () => void {
  modalBackHandlers.push(close)
  return () => {
    const index = modalBackHandlers.indexOf(close)
    if (index !== -1) modalBackHandlers.splice(index, 1)
  }
}

/**
 * Dismiss the topmost open overlay, if any. Returns true when a handler
 * consumed the press, false when nothing was open or every handler declined.
 */
export function dismissTopModal(): boolean {
  // Walk down from the top rather than popping blindly, and leave the stack
  // alone: registration is the overlay's own business (useHardwareBack drops
  // it when the overlay closes or unmounts). Popping here would unregister a
  // multi-step surface after its first back press, so onboarding would step
  // back once and then let every further press fall through to the app shell.
  for (let i = modalBackHandlers.length - 1; i >= 0; i--) {
    if (modalBackHandlers[i]() !== false) return true
  }
  return false
}

export function consumePendingAction(action: string): PendingAction | null {
  const current = pendingAction.value
  if (current && current.action === action) {
    pendingAction.value = null
    return current
  }
  return null
}
